import type { DataGridRowSelectionState, TableFilterState } from '@ibsys/kit';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CONVERSATION_IDS_FILTER_ID } from '@/entities/conversations';
import { useHasPermission } from '@/entities/profile';
import {
  SAMPLE_ID_FILTER_ID,
  getSampleTableName,
  parseSampleIdParam,
  useSample,
  type SampleResponse,
} from '@/entities/sample';
import { stableStringify } from '@/shared/lib';

/** Состояние раздела выборок: открытая выборка и набор её записей */
export type SampleSectionState = {
  /** Открытая выборка; `undefined` — список без ограничения выборкой */
  sampleId?: number;
  /** Открытая выборка целиком: тип, отбор, состав */
  sample?: SampleResponse;
  /** Имя таблицы личного вида: у каждой выборки своё */
  tableName: string;
  /** Есть разрешение на изменение выборок */
  canAdmin: boolean;
  /**
   * Отбор раздела: выборка, которой ограничен список, либо набранное — в показе набранного.
   * Пользовательские фильтры действуют поверх него и сужают показанное. У выборки по набору
   * записей вне показа набранного отбора раздела нет — список раскрыт до условий пользователя.
   */
  sectionFilters: TableFilterState;
  /** Отмеченные диалоги: состав выборки вместе с несохранёнными правками */
  pickedIds: number[];
  /** Отметки строк в виде состояния таблицы */
  rowSelection: DataGridRowSelectionState;
  onRowSelectionChange: (next: DataGridRowSelectionState) => void;
  /** Включён выбор записей — у строк списка показаны флажки */
  isPicking: boolean;
  onTogglePicking: () => void;
  /** Показ набранного: список сужен до отмеченных диалогов */
  isPickedOnly: boolean;
  onTogglePickedOnly: () => void;
  /** Состав отличается от сохранённого в выборке */
  isCompositionDirty: boolean;
};

/** Отмеченные диалоги в порядке возрастания — для сравнения составов между собой */
const compositionKeyOf = (ids: number[]): string => stableStringify([...ids].sort((a, b) => a - b));

/**
 * Состояние раздела выборок. Живёт на уровне раздела, а не секции списка: набранное переживает
 * и смену отбора, и уход в карточку диалога — иначе набор записей терялся бы там, где раскладка
 * показывает запись вместо списка.
 */
export const useSampleSectionState = (): SampleSectionState => {
  const { sampleId: sampleParam } = useParams<{ sampleId?: string }>();
  const sampleId = parseSampleIdParam(sampleParam);
  const canAdmin = useHasPermission('ADMIN_SAMPLE');
  const { data: sample } = useSample(sampleId);

  const [pickedIds, setPickedIds] = useState<number[]>([]);
  const [isPicking, setIsPicking] = useState(false);
  const [isPickedOnlyRequested, setIsPickedOnlyRequested] = useState(false);

  // Сохранённый состав выборки: он же начальные отметки — открытая выборка показывает,
  // что в неё входит, и лишнее снимается тем же флажком.
  const composition = useMemo(() => sample?.conversations?.map(({ id }) => id) ?? [], [sample]);
  const compositionKey = compositionKeyOf(composition);

  // Состав следит за сохранённым: он меняется при смене выборки и когда её состав изменил другой
  // администратор. Несохранённые отметки при этом уступают общему составу выборки. Зависимость —
  // ключ состава, а не сам массив: новая ссылка на тот же состав отметки бы не трогала.
  // biome-ignore lint/correctness/useExhaustiveDependencies: отслеживается ключ состава, а не сам массив
  useEffect(() => {
    setPickedIds(composition);
  }, [compositionKey, sampleId]);

  // Оба переключателя задаёт тип открытой выборки: в выборке по фильтрам состав отметками не
  // набирают — флажков там нет; в выборке по набору записей, наоборот, состав сразу виден
  // отмеченным и правится теми же флажками. Дальше пользователь переключает их сам, поэтому
  // зависимость — открытая выборка и её тип, а не каждое обновление её данных.
  const sampleType = sample?.type;
  useEffect(() => {
    if (sampleId == null || !sampleType) return;
    const isRecords = sampleType === 'records';
    setIsPicking(isRecords);
    setIsPickedOnlyRequested(isRecords);
  }, [sampleId, sampleType]);

  // Показ набранного держится, только пока есть что показывать и пока включён сам выбор записей:
  // без него список остался бы суженным до набранного, а снять показ было бы нечем — переключатель
  // стоит рядом с выбором записей. Признак вычисляется, а не сбрасывается вслед за набранным, —
  // иначе сброс спорил бы с включением показа по типу открытой выборки в одном и том же проходе.
  const isPickedOnly = isPickedOnlyRequested && isPicking && pickedIds.length > 0;

  const rowSelection = useMemo<DataGridRowSelectionState>(
    () => Object.fromEntries(pickedIds.map((id) => [String(id), true])),
    [pickedIds],
  );

  const onRowSelectionChange = useCallback((next: DataGridRowSelectionState) => {
    const ids = Object.entries(next)
      .filter(([, isSelected]) => isSelected)
      .map(([rowId]) => Number(rowId));
    setPickedIds(ids);
    // Снята последняя отметка — показывать в набранном нечего, и список остаётся прежним даже
    // после новой отметки: показ набранного пользователь включает сам.
    if (!ids.length) setIsPickedOnlyRequested(false);
  }, []);

  const sectionFilters = useMemo<TableFilterState>(() => {
    if (isPickedOnly) return [{ id: CONVERSATION_IDS_FILTER_ID, value: pickedIds }];
    // Список выборки по набору записей вне показа набранного её составом не ограничен: там
    // донабирают состав, а ограничение не дало бы выйти за его пределы. Пока тип выборки ещё
    // не известен, список ограничен ею — иначе в выборке на миг показались бы чужие диалоги.
    if (sampleId != null && sampleType !== 'records') return [{ id: SAMPLE_ID_FILTER_ID, value: sampleId }];
    return [];
  }, [isPickedOnly, pickedIds, sampleId, sampleType]);

  const onTogglePicking = useCallback(() => {
    setIsPicking((picking) => !picking);
  }, []);

  const onTogglePickedOnly = useCallback(() => {
    setIsPickedOnlyRequested((pickedOnly) => !pickedOnly);
  }, []);

  return {
    sampleId,
    sample,
    tableName: getSampleTableName(sampleId),
    canAdmin,
    sectionFilters,
    pickedIds,
    rowSelection,
    onRowSelectionChange,
    isPicking,
    onTogglePicking,
    isPickedOnly,
    onTogglePickedOnly,
    isCompositionDirty: sample?.type === 'records' && compositionKeyOf(pickedIds) !== compositionKey,
  };
};
